import styled, { css } from "styled-components";

export const CardWrapper = styled.div`
  height: 680px; /* Further reduced height from 680px to 650px to help avoid overlap */
  width: 520px;
  border-left: 1px solid rgba(29, 29, 29, 0.1);
  border-right: none;
  transform-origin: center top;
  transition: transform 0.2s ease-out;

  ${({ $isLast }) =>
    $isLast &&
    css`
      border-right: 1px solid rgba(29, 29, 29, 0.1);
    `}

  /*
   * Adaptive scaling for small height screens
   * 
   * PROBLEM:
   * - Cards have a fixed height of 680px
   * - On small height screens (low monitors) 
   *   cards don't fit completely and get cut off
   * - Users cannot see the full card content
   * 
   * SOLUTION:
   * - Proportional content scaling using transform: scale()
   * - 75 adaptation points with 10px screen height steps
   * - Scale ranges from 99% (1000px) to 25% (260px)
   * - Synchronized adaptation preserving all proportions
   * 
   * RESULT:
   * - Cards ALWAYS fit completely on screen
   * - All proportions and content readability are preserved
   * - Smooth transitions when window is resized
   * - Support from large monitors to extremely small screens
   */

  @media (max-height: 1000px) {
    transform: scale(0.99);
  }

  @media (max-height: 990px) {
    transform: scale(0.98);
  }

  @media (max-height: 980px) {
    transform: scale(0.97);
  }

  @media (max-height: 970px) {
    transform: scale(0.96);
  }

  @media (max-height: 960px) {
    transform: scale(0.95);
  }

  @media (max-height: 950px) {
    transform: scale(0.94);
  }

  @media (max-height: 940px) {
    transform: scale(0.93);
  }

  @media (max-height: 930px) {
    transform: scale(0.92);
  }

  @media (max-height: 920px) {
    transform: scale(0.91);
  }

  @media (max-height: 910px) {
    transform: scale(0.9);
  }

  @media (max-height: 900px) {
    transform: scale(0.89);
  }

  @media (max-height: 890px) {
    transform: scale(0.88);
  }

  @media (max-height: 880px) {
    transform: scale(0.87);
  }

  @media (max-height: 870px) {
    transform: scale(0.86);
  }

  @media (max-height: 860px) {
    transform: scale(0.85);
  }

  @media (max-height: 850px) {
    transform: scale(0.84);
  }

  @media (max-height: 840px) {
    transform: scale(0.83);
  }

  @media (max-height: 830px) {
    transform: scale(0.82);
  }

  @media (max-height: 820px) {
    transform: scale(0.81);
  }

  @media (max-height: 810px) {
    transform: scale(0.8);
  }

  @media (max-height: 800px) {
    transform: scale(0.79);
  }

  @media (max-height: 790px) {
    transform: scale(0.78);
  }

  @media (max-height: 780px) {
    transform: scale(0.77);
  }

  @media (max-height: 770px) {
    transform: scale(0.76);
  }

  @media ${({ theme }) => theme.media.mobile} {
    height: 100%;
    width: 100%;
    border-right: 1px solid rgba(29, 29, 29, 0.1);
  }
`;

export const ContentContainer = styled.div`
  width: 100%;
  padding: 0 40px 0 40px;

  @media ${({ theme }) => theme.media.mobile} {
    padding: 0 20px 0 20px;
  }
`;

export const Title = styled.h3`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 500;
  font-size: 36px;
  line-height: 130%;
  color: rgba(29, 29, 29, 0.85);
  padding-top: 60px; /* Increased padding-top from 40px to 60px to create more space */
  position: relative;

  @media ${({ theme }) => theme.media.mobile} {
    font-size: 22px;
  }
`;

export const TitleDot = styled.img`
  margin-bottom: -1px;
  position: relative;
  margin-left: 6px;
  width: 7px;
  height: 7px;
`;

export const DetailsContainer = styled.div`
  margin-top: 25px; /* Slightly adjusted from 30px to 25px */
  padding-bottom: 30px; /* Kept at 30px from previous change */

  @media ${({ theme }) => theme.media.mobile} {
    margin-top: 20px; /* Kept at 20px from previous change */
  }
`;

export const Image = styled.img`
  aspect-ratio: 1;
  object-fit: contain;
  width: 100px;
  max-width: 100%;
  border-radius: 12px;
`;

const BaseSection = styled.div`
  margin-top: 24px;

  @media ${({ theme }) => theme.media.mobile} {
    margin-top: 16px;
  }
`;

export const ProblemSection = styled(BaseSection)`
  margin-top: 0; /* Removed top margin to reduce space after title */
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

export const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
`;

export const Icon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const BaseSectionHeaderText = css`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 16px;
  line-height: 150%;

  @media ${({ theme }) => theme.media.mobile} {
    font-size: 14px;
  }
`;

export const SectionHeaderText = styled.div`
  ${BaseSectionHeaderText}
  color: rgba(29, 29, 29, 0.5);
`;

const BaseText = css`
  margin-top: 8px;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 400;
  font-size: 20px;
  line-height: 150%;

  @media ${({ theme }) => theme.media.mobile} {
    font-size: 16px;
  }
`;

export const ProblemText = styled.div`
  ${BaseText}
  color: rgba(29, 29, 29, 0.5);
`;

const commonDividerStyle = css`
  border-top: 1px solid rgba(29, 29, 29, 0.1);
  min-height: 1px;
  width: 100%;
`;

export const Divider = styled.div`
  @media ${({ theme }) => theme.media.mobile} {
    ${commonDividerStyle}
    margin-top: 16px;
  }
`;

export const DividerBlock = styled.div`
  ${commonDividerStyle}
  margin-top: 24px;

  @media ${({ theme }) => theme.media.mobile} {
    margin-top: 16px;
  }
`;

export const SolutionSection = styled(BaseSection)`
  display: flex;
  flex-direction: column;
`;

export const SolutionHeader = styled(SectionHeader)``;

export const SolutionHeaderText = styled.span`
  ${BaseSectionHeaderText}
  font-weight: 600;
  color: rgba(29, 29, 29, 0.85);
`;

export const SolutionText = styled.div`
  ${BaseText}
  color: rgba(29, 29, 29, 0.85);
`;
